import { z } from "zod";
/**
 * Const-object "enums" mirroring the Prisma enums described in spec §4.
 *
 * We deliberately do NOT use TypeScript `enum`: it emits a runtime construct that
 * does not survive `isolatedModules`-style transpilation cleanly and it cannot be
 * used as a plain string in a JSON payload without a cast. A frozen const object
 * plus a derived union type gives the same ergonomics with none of that.
 *
 * Adding a value here is an `ALTER TABLE` on MariaDB (spec §1) — keep them in
 * lockstep with `prisma/schema.prisma`.
 *
 * NOTE: `SupplyType` and `PricingMode` are NOT redefined here. They are owned by
 * `@steelbill/tax` and re-exported from `src/index.ts`. Only their runtime value
 * tuples and Zod schemas live in this file, guarded by the identity assertions
 * below so the two can never drift apart.
 */
/** Builds a frozen `{ A: "A", B: "B" }` from `["A", "B"] as const`. */
function enumFrom(values) {
    const out = {};
    for (const value of values) {
        out[value] = value;
    }
    return Object.freeze(out);
}
/* -------------------------------------------------------------------------- */
/* Supply type — computed by the tax engine, persisted on the invoice (§4)      */
/* -------------------------------------------------------------------------- */
export const SUPPLY_TYPES = ["INTRA_STATE", "INTER_STATE"];
export const SupplyTypeSchema = z.enum(SUPPLY_TYPES);
/* -------------------------------------------------------------------------- */
/* Pricing mode (§4, §5). LUMPSUM is the dominant real-world case.              */
/* -------------------------------------------------------------------------- */
export const PRICING_MODES = ["LUMPSUM", "BY_PIECE", "BY_WEIGHT", "BY_LENGTH"];
export const PricingModeSchema = z.enum(PRICING_MODES);
/* -------------------------------------------------------------------------- */
/* Item category (§4) — default SERVICE                                         */
/* -------------------------------------------------------------------------- */
export const ITEM_CATEGORIES = ["SERVICE", "MS", "SS", "FABRICATED", "HARDWARE", "OTHER"];
export const ItemCategory = enumFrom(ITEM_CATEGORIES);
export const ItemCategorySchema = z.enum(ITEM_CATEGORIES);
/* -------------------------------------------------------------------------- */
/* Supply nature (§8.1) — goods vs works contract. Tax math is identical in v1;  */
/* the tag drives HSN-vs-SAC printing and GSTR-1 reporting.                     */
/* -------------------------------------------------------------------------- */
export const SUPPLY_NATURES = ["TRADED_GOODS", "FABRICATED_GOODS", "WORKS_CONTRACT"];
export const SupplyNature = enumFrom(SUPPLY_NATURES);
export const SupplyNatureSchema = z.enum(SUPPLY_NATURES);
/* -------------------------------------------------------------------------- */
/* Document statuses (§4)                                                       */
/* -------------------------------------------------------------------------- */
/**
 * §7.1 — `PARTIALLY_CONVERTED` is a real, reachable state, not a placeholder: a
 * customer may order only part of a quote, so lines can be deselected before
 * converting and one quotation can produce several invoices. The quotation stays
 * PARTIALLY_CONVERTED while any line remains uninvoiced and only reaches
 * CONVERTED once none do. Order mirrors `prisma/schema.prisma`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `VOID` — added 2026-08-13 (§4). A quotation the owner saved by mistake: the
 * wrong customer, the wrong job, a rate with a digit missing.
 *
 * IT REPLACED A HARD DELETE, and that is the whole point of it being a status at
 * all. `POST /quotations/:id/void` supersedes the `DELETE /quotations/:id` an
 * earlier build shipped. §4 gives three reasons and each one is a thing that
 * would otherwise go wrong:
 *  - the series `NNN/YY` is CONTINUOUS (§6), so removing the row left a hole —
 *    a customer ringing about "542/26" got "we have no such quotation" instead
 *    of "that one was cancelled", and §17.4's CA export cannot report a serial
 *    range with a silent gap in it;
 *  - a voided quotation is still EVIDENCE of what was quoted, to whom, on what
 *    date. A hard delete kept that only in `audit_log`, where nothing in the app
 *    can show it to him;
 *  - one rule — *nothing here is destroyed, things are marked void* — is a rule
 *    the owner can hold in his head. Two rules, one for bills and another for
 *    quotations, is a thing he has to remember.
 *
 * IT IS TERMINAL. No transition leaves it (see `QUOTATION_STATUS_TRANSITIONS` in
 * apps/api), and it is absent from {@link MANUAL_QUOTATION_STATUSES} so the
 * status endpoint cannot reach it in either direction. Un-voiding would put a
 * number back into circulation after the owner has told a customer it was
 * cancelled; reviving one means copying it, which allocates a new number
 * honestly.
 *
 * Adding this value was an `ALTER TABLE` on MariaDB (spec §1.1, CLAUDE.md) —
 * see prisma/sql/04_quotation_void.sql.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const QUOTATION_STATUSES = [
    "DRAFT",
    "SENT",
    "APPROVED",
    "REJECTED",
    "PARTIALLY_CONVERTED",
    "CONVERTED",
    "EXPIRED",
    "VOID",
];
export const QuotationStatus = enumFrom(QUOTATION_STATUSES);
export const QuotationStatusSchema = z.enum(QUOTATION_STATUSES);
/**
 * PENDING_IRN is reserved and unused in v1 — turnover is far below the ₹5 crore
 * e-invoicing threshold, so invoices go DRAFT → ISSUED directly (spec §10.1).
 */
export const INVOICE_STATUSES = ["DRAFT", "PENDING_IRN", "ISSUED", "CANCELLED"];
export const InvoiceStatus = enumFrom(INVOICE_STATUSES);
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
/* -------------------------------------------------------------------------- */
/* Payments — §17.5, behind the `payments` flag                                 */
/* -------------------------------------------------------------------------- */
/**
 * NOTE THAT `INVOICE_STATUSES` ABOVE GAINED NOTHING. There is no `PAID` and no
 * `PARTLY_PAID`, and there never will be — a payment is a row in `payments`
 * (§17.5, `model Payment`), not a state of the tax invoice. An enum value cannot
 * hold "₹50,000 received against ₹1,45,600", which is the ordinary case in this
 * trade; it would record THAT money arrived while losing HOW MUCH, WHEN and BY
 * WHAT MEANS, which is the only part anyone ever needs. And `status` is the
 * document's LEGAL state: ISSUED and CANCELLED are facts about the invoice,
 * being paid is a fact about the bank, and §17.3 exists precisely because an
 * issued invoice is not mutated.
 */
/**
 * How the money arrived. Mirrors `enum PaymentMethod` in prisma/schema.prisma
 * EXACTLY AND IN ORDER — a MariaDB `ENUM` stores the ordinal, so a value
 * inserted in the middle of this tuple without the matching `ALTER TABLE` would
 * silently re-label existing rows.
 *
 * `TDS` IS A METHOD, AND THAT IS THE ANSWER TO THE OBVIOUS OBJECTION. Tax
 * deducted at source is not money that arrived in the bank — but it IS money
 * the customer has discharged, and it settles the invoice just as a NEFT does.
 * Without a way to record it, every bill a customer deducted 2% from stays
 * permanently short of its grand total, the overpayment guard never lets it
 * reach zero, and the home screen's "who owes me money" figure fills up with
 * amounts nobody owes. The certificate number goes in `reference`.
 *
 * `OTHER` is the honest escape hatch, not a dumping ground: adding a value here
 * is an `ALTER TABLE` (CLAUDE.md), so a method that shows up twice a year is
 * better recorded as OTHER with a note than as a migration.
 */
export const PAYMENT_METHODS = [
    "CASH",
    "CHEQUE",
    "NEFT",
    "RTGS",
    "IMPS",
    "UPI",
    "TDS",
    "OTHER",
];
export const PaymentMethod = enumFrom(PAYMENT_METHODS);
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
/**
 * How much of an invoice has been received — **DERIVED, NEVER STORED** (§17.5).
 *
 * There is no column behind this and there must not be one. It is computed, on
 * every read, as `grand_total_paise` minus `SUM(payments.amount_paise)`; a
 * denormalised copy and the ledger WILL disagree eventually, and the ledger is
 * the one that is right.
 *
 * It is NOT a Prisma enum, so it needs no migration — which is the point of
 * deriving it. Present here rather than in a route file because the SPA renders
 * these three words and must not invent a fourth.
 */
export const PAYMENT_STATES = ["UNPAID", "PARTLY_PAID", "PAID"];
export const PaymentState = enumFrom(PAYMENT_STATES);
export const PaymentStateSchema = z.enum(PAYMENT_STATES);
/* -------------------------------------------------------------------------- */
/* Delivery challans — §17.7, Rule 55 CGST                                      */
/* -------------------------------------------------------------------------- */
/**
 * WHY the goods are moving. Rule 55(1) permits goods to travel without a tax
 * invoice only for specific reasons, and the reason has to be stated on the
 * document — so this is not a category for reporting, it is the legal basis for
 * the movement.
 *
 * The owner confirmed on 2026-08-15 that the first two happen TODAY, with no
 * document at all: material out for galvanising and painting, customer material
 * in for fabrication.
 */
export const CHALLAN_TYPES = [
    "JOB_WORK_OUT",
    "JOB_WORK_IN",
    "SUPPLY_NOT_INVOICED",
    "APPROVAL",
    "RETURNABLE",
    "OTHER",
];
export const ChallanType = enumFrom(CHALLAN_TYPES);
export const ChallanTypeSchema = z.enum(CHALLAN_TYPES);
export function challanDirection(type) {
    return type === "JOB_WORK_IN" ? "INWARD" : "OUTWARD";
}
/**
 * Whether the goods are expected back, defaulted from the type.
 *
 * Only `SUPPLY_NOT_INVOICED` is a one-way trip: the work has been delivered and
 * an invoice follows. Everything else comes back — processed material from the
 * galvaniser, the customer's own material once fabricated, goods the customer
 * declined, and tools.
 *
 * A DEFAULT, NOT A RULE. The API seeds `isReturnable` with this and then stores
 * whatever it was told, because the galvaniser sometimes keeps the offcuts and
 * the owner should not have to argue with the form about it.
 */
export function defaultIsReturnable(type) {
    return type !== "SUPPLY_NOT_INVOICED";
}
/**
 * A challan is NOT a tax document, so none of these states affect any liability
 * (§17.7). They record where the goods physically are.
 */
export const CHALLAN_STATUSES = ["OPEN", "RETURNED", "INVOICED", "CANCELLED"];
export const ChallanStatus = enumFrom(CHALLAN_STATUSES);
export const ChallanStatusSchema = z.enum(CHALLAN_STATUSES);
/**
 * The states a challan can no longer be edited or advanced from.
 *
 * INVOICED IS TERMINAL BECAUSE A TAX DOCUMENT NOW DEPENDS ON IT. Editing the
 * lines of a challan an invoice was raised against would leave the two disagreeing
 * about what was delivered, and the invoice is the one that cannot be edited
 * (§17.3, CLAUDE.md). RETURNED is terminal because the movement is over.
 */
export const CLOSED_CHALLAN_STATUSES = [
    "RETURNED",
    "INVOICED",
    "CANCELLED",
];
export function isChallanEditable(status) {
    return !CLOSED_CHALLAN_STATUSES.includes(status);
}
/* -------------------------------------------------------------------------- */
/* Credit notes — §17.3, section 34(1) CGST                                     */
/* -------------------------------------------------------------------------- */
/**
 * TWO STATES, AND THE MISSING ONES ARE THE DESIGN.
 *
 * NO `DRAFT`. A credit note is short — a reason, a handful of lines already
 * known from the bill it reduces — and it is typed and issued in one sitting.
 * There is nothing for a half-finished one to be FOR, and `DraftKind` is a
 * database enum, so adding a draft later is a deliberate `ALTER TABLE` rather
 * than a state that exists because it was cheap.
 *
 * NO `ISSUED → something` PROGRESSION EITHER. Unlike a challan there is no
 * physical event to record afterwards: the moment the note exists, the bill is
 * reduced. The only transition is the one every document in this system has —
 * CANCELLED, which retains the number (§2) and, because every sum over this
 * table counts ISSUED rows only, releases the credited amount back into what the
 * customer owes.
 *
 * `ISSUED` IS THE DEFAULT AND THE ONLY CREATABLE VALUE. `POST /credit-notes`
 * never accepts a status; the schema has no such field.
 */
export const CREDIT_NOTE_STATUSES = ["ISSUED", "CANCELLED"];
export const CreditNoteStatus = enumFrom(CREDIT_NOTE_STATUSES);
export const CreditNoteStatusSchema = z.enum(CREDIT_NOTE_STATUSES);
/**
 * Which credit notes actually reduce a bill.
 *
 * ONE CONSTANT, READ BY EVERY SUM — the service's `sumCreditNotes`, the
 * outstanding register's `groupBy`, and the "have we credited too much?" guard.
 * A cancelled credit note credits nothing, and the failure mode of restating
 * that filter per call site is the one §17.5 already paid for: a figure that
 * disagrees with the list it opens.
 */
export const CREDITING_NOTE_STATUSES = [
    "ISSUED",
];
/* -------------------------------------------------------------------------- */
/* The document types this system keeps a register of                          */
/* -------------------------------------------------------------------------- */
/**
 * EVERY KIND OF DOCUMENT THIS BUSINESS ISSUES, spelled once (2026-08-16).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS, AND IT IS NOT TIDINESS. Invoices and quotations were built
 * together, so every surface that has to name "the kinds of document" was
 * written when there were exactly two — as a ternary, a pair of `findMany`s, or
 * a hand-typed `"invoice" | "quotation"`. Delivery challans (§17.7) arrived on
 * 2026-08-15, and the two-armed shapes did not fail; they took the else-branch.
 * A challan draft opened the QUOTATION editor. The home screen's Recent card
 * showed challans in its drafts half and not in its documents half. The scanner
 * said "nothing found" about a barcode this app had printed. Each was found by
 * the owner noticing something missing on his screen, which is the most
 * expensive way a list can stop being complete.
 *
 * SO THE FIX IS A KEY, NOT A THIRD BRANCH. A `Record<DocumentKind, …>` keyed by
 * this union, or a list derived from {@link DOCUMENT_KINDS}, FAILS THE BUILD
 * when a kind is added — at the cheapest possible moment, in exactly the places
 * that have to be decided. `MOUNTED_DRAFT_KINDS` below already proved the
 * mechanism works; this is the same idea for documents rather than drafts.
 *
 * CREDIT NOTES (§17.3) WERE THE TEST OF THIS, AND IT PASSED (2026-08-16).
 * Adding `"creditNote"` broke the compile in exactly seven places and nowhere
 * else: the API's `mergeRecentDocuments` and `LINE_REGISTERS`, and the SPA's
 * `DOCUMENT_KIND_ICON` / `DOCUMENT_KIND_HREF` / `DOCUMENT_AMOUNT_STYLE`,
 * `DOCUMENT_REGISTERS` and `KIND_OVERRIDES`. Every one of them was a real
 * decision — which endpoint, which route, whether the amount is money owed —
 * and every one was made at the moment the kind was invented rather than the
 * morning the owner noticed a row was missing. The next kind gets the same
 * treatment; do not weaken any of those maps into a lookup with a default.
 *
 * LOWERCASE, BECAUSE THIS IS THE WIRE FORM. It is what `DashboardDocumentCard.
 * kind`, `SearchHit.kind` and `ChallanDetail.kind` already put on the wire, and
 * what a client `Record` is keyed by. The SHOUTING forms are different things
 * and must not be conflated: {@link DraftKind} (`"INVOICE"`) is a *saved form*
 * with no number, and the Prisma `entity_type` on an audit row is a table name.
 *
 * NOT A CAPABILITY, NOT A FLAG, AND NOT A ROUTE. Naming a kind here says the
 * register exists; it says nothing about who may read it (§2.1 — that is
 * `can()`) or whether the module is switched on (§18 — that is `isFlagEnabled`).
 * Every consumer still has to answer both, and the ones that must gate challans
 * do (see `readRecentChallans` in apps/api/src/routes/dashboard.ts).
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const DOCUMENT_KINDS = ["invoice", "quotation", "challan", "creditNote"];
export const DocumentKind = enumFrom(DOCUMENT_KINDS);
export const DocumentKindSchema = z.enum(DOCUMENT_KINDS);
/* -------------------------------------------------------------------------- */
/* Unfinished documents — a saved FORM, not a document (§7.2 amendment)         */
/* -------------------------------------------------------------------------- */
/**
 * Which editor a `document_drafts` row belongs to (§7.2 amendment, 2026-08-15).
 *
 * ONE TABLE FOR BOTH KINDS, so this column is what tells them apart. A draft has
 * no number, no totals and no legal standing whatever its kind; the only things
 * that differ are which editor reopens it and which capability guards it, and
 * both of those are decided from this value. Two tables would have meant two
 * routers, two hooks and two migrations every time the draft mechanism changed —
 * and the second one would be the one that got forgotten.
 *
 * It is also a SECURITY boundary, not just a label: `/api/v1/invoices/drafts`
 * and `/api/v1/quotations/drafts` are separate mounts behind different flags and
 * different capabilities, and every query on either is scoped by this value. A
 * read that ignored it would hand the quotation editor a bill's lines.
 */
/*
 * ALL THREE ARE REAL AS OF 2026-08-15, and this tuple must stay in step with
 * `enum DraftKind` in prisma/schema.prisma — which lists the same three.
 *
 * CHALLAN WAS HERE BEFORE IT WAS BUILT, deliberately: the value existed in the
 * DB enum from the start because MariaDB cannot add one without an `ALTER TABLE`
 * (§1.1), and it existed here because a types package that disagrees with the
 * schema is worse than either. That was the bet, and it paid — when §17.7
 * landed, the `Record<DraftKind, …>` lookups elsewhere are exactly what pointed
 * at the missing flag, capability and mount. The narrowing alias that made the
 * bet safe is {@link MountedDraftKind}, immediately below; keep it for §17.3.
 */
export const DRAFT_KINDS = ["INVOICE", "QUOTATION", "CHALLAN"];
export const DraftKind = enumFrom(DRAFT_KINDS);
export const DraftKindSchema = z.enum(DRAFT_KINDS);
export const MOUNTED_DRAFT_KINDS = [
    "INVOICE",
    "QUOTATION",
    "CHALLAN",
];
/**
 * How finished the person who saved a draft says it is (§7.2 amendment).
 *
 * NOT A DOCUMENT STATUS, and the distinction is the whole point. An INVOICE is
 * DRAFT → ISSUED → CANCELLED ({@link INVOICE_STATUSES}) and a QUOTATION runs
 * DRAFT → SENT → … ({@link QUOTATION_STATUSES}); those describe a NUMBERED
 * DOCUMENT THAT EXISTS. This describes a form that has no number yet. Neither
 * document enum gained a value here and neither may: READY lives on the saved
 * form precisely so that it costs nothing and commits nobody.
 *
 * Without it a drafts list cannot tell a half-typed bill from one that was
 * checked at the site and left for someone at the shop to issue — and issuing is
 * the irreversible act that spends a number (§6), so "is this one ready?" is
 * exactly the question worth recording an answer to.
 *
 * Mirrors `enum DraftStatus` in prisma/schema.prisma; a value added here is an
 * `ALTER TABLE` on MariaDB (CLAUDE.md), so keep the two in lockstep.
 */
export const DRAFT_STATUSES = ["DRAFT", "READY"];
export const DraftStatus = enumFrom(DRAFT_STATUSES);
export const DraftStatusSchema = z.enum(DRAFT_STATUSES);
/* -------------------------------------------------------------------------- */
/* Users (§2)                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * §2. The three people the system has accounts for: the father (OWNER), the son
 * (ADMIN) and the CA (ACCOUNTANT).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ACCOUNTANT IS IMPLEMENTED AS OF 2026-08-17. `IMPLEMENTED_USER_ROLES` IS GONE.
 *
 * It used to be a SECOND, SHORTER list (`OWNER | ADMIN`) that decided whether a
 * role could hold a session at all, because §2 defined the CA role without
 * building it. The owner has since decided it: "CA WILL have their login and
 * their view." So the shorter list has been deleted rather than quietly widened
 * to three, and the reason is that a list of "roles we have built" and a list of
 * "roles that exist" which are always equal is a list that only ever drifts.
 *
 * WHAT REPLACED IT IS NOT ANOTHER LIST OF ROLES. Availability is now a property
 * of the role, resolved through `ROLE_FEATURE_FLAGS` in permissions.ts: a role
 * either needs a feature flag to be on or it does not. That is one rule, in one
 * place, and it can be answered for a role nobody has thought of yet.
 *
 * The two §2 constraints on the CA are unchanged and are enforced by the
 * capability map, not by keeping her logged out:
 *  - STRICTLY READ-ONLY ON DOCUMENT CONTENT. An issued GST invoice is not
 *    amendable; a correction is a credit note (§17.3), never an edit.
 *  - Every action still writes an `audit_log` row (§13), including exports.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const USER_ROLES = ["OWNER", "ADMIN", "ACCOUNTANT"];
export const UserRole = enumFrom(USER_ROLES);
export const UserRoleSchema = z.enum(USER_ROLES);
/* -------------------------------------------------------------------------- */
/* PDF rendering options (§9.2, §9.3)                                           */
/* -------------------------------------------------------------------------- */
export const COPY_TYPES = ["ORIGINAL", "DUPLICATE", "TRIPLICATE"];
export const CopyType = enumFrom(COPY_TYPES);
export const CopyTypeSchema = z.enum(COPY_TYPES);
/**
 * Quotation tax presentation (§9.3). The existing Excel quotations print
 * "SGST 9% EXTRA" rather than computing tax, so TAX_EXTRA is the default and
 * the printed "G. Total" is the PRE-TAX figure.
 *
 * `TAX_COMPUTED` (renamed from the older `TAX_INCLUSIVE_SHOWN`, which read as
 * "the rate is tax-inclusive" — it never was) computes CGST/SGST/IGST and
 * prints a true grand total. Conversion to an invoice always switches to the
 * computed presentation (§7.1).
 */
export const TAX_DISPLAY_MODES = ["TAX_EXTRA", "TAX_COMPUTED"];
export const TaxDisplayMode = enumFrom(TAX_DISPLAY_MODES);
export const TaxDisplayModeSchema = z.enum(TAX_DISPLAY_MODES);
//# sourceMappingURL=enums.js.map